/**
 * Turn a schedule plan into compact experience rows, then label outcomes.
 * Free text from the model is reduced to a reason code before it is stored.
 */
import { distanceBand } from "./inboxMintMemory";
import { compactReasonCode, distillDue, distillPolicy, type LearnedPolicy } from "./learnedPolicy";
import {
  appendExperience,
  compactExperienceContext,
  hourBandFor,
  paxBandFor,
  type ExperienceAction,
  type ExperienceActor,
  type ExperienceRecord,
} from "./experienceLog";
import type { GrokScheduleAction, GrokSchedulePlan } from "./grokDispatchBrain";
import type { TripAssignment } from "./assignment";
import type { AircraftSpec, CharterRequest, Mission, OpsDeclineReason } from "./types";

const LOGGED = new Set<GrokScheduleAction["kind"]>(["accept_assign", "decline", "reschedule", "reassign_tail", "reassign_crew", "flag_review"]);

function actionOf(kind: GrokScheduleAction["kind"]): ExperienceAction {
  if (kind === "accept_assign") return "accept_assign";
  if (kind === "decline") return "decline";
  if (kind === "reschedule") return "reschedule";
  if (kind === "reassign_tail") return "reassign_tail";
  if (kind === "reassign_crew") return "reassign_crew";
  return "flag_review";
}

export function appendPlanExperience(
  log: readonly ExperienceRecord[] | null | undefined,
  plan: GrokSchedulePlan,
  input: {
    actor: ExperienceActor;
    shadow?: boolean;
    inbox: readonly CharterRequest[];
    missions: readonly Mission[];
    fleet: readonly AircraftSpec[];
    reasons: readonly OpsDeclineReason[] | null | undefined;
    now?: Date;
  },
): ExperienceRecord[] {
  const now = input.now ?? new Date();
  let next = [...(log ?? [])];
  for (const action of plan.actions) {
    if (!LOGGED.has(action.kind)) continue;
    if (!input.shadow && !action.apply && action.kind !== "flag_review") continue;
    const request = action.requestId ? input.inbox.find((row) => row.id === action.requestId) : undefined;
    const mission = action.missionId
      ? input.missions.find((row) => row.id === action.missionId)
      : request?.acceptedMissionId
        ? input.missions.find((row) => row.id === request.acceptedMissionId)
        : undefined;
    const aircraftId = action.aircraftId || mission?.aircraftId || request?.suggestedAircraftId || "";
    const spec = input.fleet.find((row) => row.id === aircraftId);
    const origin = request?.originIcao || mission?.originIcao || "";
    const dest = request?.destIcao || mission?.destIcao || "";
    const departure = action.departureIso || request?.departureIso || mission?.departureIso || "";
    const previous = mission?.departureIso || request?.departureIso || "";
    const slip = action.departureIso && previous ? Math.round((Date.parse(action.departureIso) - Date.parse(previous)) / 60_000) : undefined;
    const reasonCode = compactReasonCode(input.reasons, action.reason);
    next = appendExperience(next, {
      action: actionOf(action.kind),
      requestId: action.requestId,
      missionId: mission?.id || action.missionId,
      atUtc: now.toISOString(),
      outcome: input.shadow ? "shadow" : "proposed",
      shadow: input.shadow,
      distanceBand: distanceBand(request?.distanceNm ?? mission?.distanceNm) ?? "",
      slipMin: Number.isFinite(slip) ? slip : undefined,
      clashKind: action.kind === "reschedule" ? "overlap" : "",
      chosenCrewId: action.crewId || "",
      reasonCode,
      context: compactExperienceContext({
        actor: input.actor,
        mission: mission ?? null,
        crewId: action.crewId,
      }),
      features: {
        originIcao: origin,
        destIcao: dest,
        flightType: request?.flightType || mission?.flightType || "",
        paxBand: paxBandFor(request?.paxCount ?? mission?.paxCount),
        hourBand: hourBandFor(departure),
        aircraftId,
        icaoType: spec?.icaoType ?? "",
        declineReasonId: action.kind === "decline" ? reasonCode : "",
      },
    });
  }
  return next;
}

export function refreshExperienceOutcomes(
  log: readonly ExperienceRecord[] | null | undefined,
  input: {
    missions: readonly Mission[];
    assignments: readonly TripAssignment[];
    clashMissionIds: readonly string[];
    now?: Date;
  },
): ExperienceRecord[] {
  const now = input.now ?? new Date();
  const clashes = new Set(input.clashMissionIds);
  return (log ?? []).map((row) => {
    if (row.shadow || !row.missionId) return row;
    if (row.outcome === "reverted") return row;
    const mission = input.missions.find((item) => item.id === row.missionId);
    if (!mission) return row;
    const age = now.getTime() - Date.parse(row.atUtc);
    const within = Number.isFinite(age) && age >= 0 && age <= 24 * 60 * 60 * 1000;
    const crewId = input.assignments.find((item) => item.missionId === mission.id)?.crewId ?? "";
    const stdChanged = row.context?.trip?.departureIso && row.context.trip.departureIso !== mission.departureIso;
    const tailChanged = Boolean(row.features.aircraftId) && row.features.aircraftId !== mission.aircraftId;
    const crewChanged = Boolean(row.chosenCrewId) && crewId && row.chosenCrewId !== crewId;
    if (within && (stdChanged || tailChanged || crewChanged)) return { ...row, outcome: "reverted" };
    if (mission.status === "cancelled") {
      const reason = (mission.cancelReason || "other").replace(/[^a-z0-9_-]/gi, "").slice(0, 32).toLowerCase() || "other";
      return { ...row, outcome: `cancelled:${reason}` };
    }
    const delay = mission.delays?.find((item) => item.status === "confirmed");
    if (delay) return { ...row, outcome: `delayed:${delay.code}` };
    if (clashes.has(mission.id) && row.outcome === "proposed") return { ...row, outcome: "clashed_later" };
    if (row.outcome === "proposed" && Number.isFinite(age) && age > 24 * 60 * 60 * 1000) return { ...row, outcome: "kept" };
    return row;
  });
}

export function recordBrainPass(input: {
  log: readonly ExperienceRecord[] | null | undefined;
  plan: GrokSchedulePlan;
  shadow: GrokSchedulePlan | null;
  inbox: readonly CharterRequest[];
  missions: readonly Mission[];
  assignments: readonly TripAssignment[];
  fleet: readonly AircraftSpec[];
  reasons: readonly OpsDeclineReason[] | null | undefined;
  clashMissionIds: readonly string[];
  policy: LearnedPolicy | null;
  distilledAt: string | null;
  now?: Date;
}): { log: ExperienceRecord[]; policy: LearnedPolicy | null; distilledAt: string | null } {
  const now = input.now ?? new Date();
  const actor: ExperienceActor = input.plan.usedGrok ? "grok" : "classic";
  let log = appendPlanExperience(input.log, input.plan, {
    actor,
    inbox: input.inbox,
    missions: input.missions,
    fleet: input.fleet,
    reasons: input.reasons,
    now,
  });
  if (input.shadow && input.plan.usedGrok) {
    log = appendPlanExperience(log, input.shadow, {
      actor: "classic",
      shadow: true,
      inbox: input.inbox,
      missions: input.missions,
      fleet: input.fleet,
      reasons: input.reasons,
      now,
    });
  }
  log = refreshExperienceOutcomes(log, {
    missions: input.missions,
    assignments: input.assignments,
    clashMissionIds: input.clashMissionIds,
    now,
  });
  if (!distillDue(input.distilledAt, now)) return { log, policy: input.policy, distilledAt: input.distilledAt };
  const policy = distillPolicy(log, now);
  return { log, policy, distilledAt: now.toISOString() };
}
